import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PogLogo from "./PogLogo";

const AGE_KEY = "pog_age_verified_v1";

const AgeGate = ({ onVerified }) => {
  const [open, setOpen] = useState(false);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    try {
      const ok = localStorage.getItem(AGE_KEY);
      if (ok === "true") {
        onVerified?.();
      } else {
        setOpen(true);
      }
    } catch (_) {
      setOpen(true);
    }
  }, [onVerified]);

  const handleYes = async () => {
    try {
      localStorage.setItem(AGE_KEY, "true");
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${BACKEND_URL}/api/age-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ verified: true }),
      }).catch(() => {});
    } catch (_) {}
    setOpen(false);
    onVerified?.();
  };

  const handleNo = () => setDenied(true);

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="age-gate"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed inset-0 z-[100] flex items-center justify-center"
        data-testid="age-gate-overlay"
      >
        {/* Deep cinematic backdrop */}
        <div
          className="absolute inset-0 bg-cover bg-center scale-105"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=2400&q=80')",
          }}
        />
        <div className="absolute inset-0 bg-[#14100D]/85 backdrop-blur-md" />
        <div className="absolute inset-0 grain pointer-events-none" />

        {/* Card */}
        <motion.div
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 max-w-lg w-[90%] mx-auto p-10 md:p-16 text-center"
          style={{
            background: "rgba(20, 16, 13, 0.7)",
            backdropFilter: "blur(24px) saturate(150%)",
            border: "1px solid rgba(239, 231, 214, 0.15)",
          }}
          data-testid="age-gate-card"
        >
          <div className="flex items-center justify-center gap-4 mb-10">
            <span className="w-10 hairline-strong h-px inline-block" />
            <PogLogo size={44} />
            <span className="w-10 hairline-strong h-px inline-block" />
          </div>

          <div className="overline mb-8" data-testid="age-gate-overline">
            Est. 1971 · Made in Hawai‘i
          </div>

          <div className="display-tight text-5xl md:text-6xl text-[#EFE7D6] mb-8">
            Aloha.
          </div>
          <p className="font-body text-xl md:text-2xl text-[#DCCFB4]/90 mb-12 leading-[1.5]">
            Are you of legal drinking age?
          </p>

          <span className="hairline block mb-10" />

          {!denied ? (
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={handleYes}
                className="btn-primary is-solid"
                data-testid="age-gate-yes-button"
              >
                Yes, I am 21+
              </button>
              <button
                onClick={handleNo}
                className="btn-ghost"
                data-testid="age-gate-no-button"
              >
                I am under 21
              </button>
            </div>
          ) : (
            <div
              className="font-body text-xl text-[#DCCFB4]"
              data-testid="age-gate-denied"
            >
              Mahalo for visiting. Come back when you're 21+.
            </div>
          )}

          <div className="mt-12 text-[9px] tracking-[0.28em] uppercase text-[#DCCFB4]/40 font-body leading-relaxed">
            You must be 21 or older to enter · Please drink responsibly
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AgeGate;
